import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { BLOCKING_LABEL } from "@/components/settings/EmailProviderCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MODE_LABEL = { simulate: "Simulado (no sale ningún correo)", qa: "PREVIEW: real solo a correos QA", live: "Envío real" };
const PROVIDER_LABEL = { accepted: "Aceptado por Resend", sent: "Enviado", delayed: "Demorado", delivered: "Entregado", bounced: "Rebotó", complained: "Marcado como spam", failed: "Falló en Resend" };
const rowStatus = (r) => (r.delivery_mode === "real" && r.provider_status ? PROVIDER_LABEL[r.provider_status] || r.provider_status : STATUS_LABEL[r.status] || r.status);
const STATUS_LABEL = {
  pending: "Pendiente", sending: "Enviando", retrying: "Reintentando", sent: "Enviado", simulated: "Simulado",
  failed: "Falló", skipped: "No enviado", superseded: "Reemplazado",
};
// "Enviado" only for real sends; simulated rows always read "Simulado".
const REASON_LABEL = {
  missing_email: "Cliente sin correo", invalid_email: "Correo inválido", simulate_mode: "Modo simulado",
  not_in_qa_allowlist: "Fuera de lista QA", domain_not_verified: "Dominio sin verificar",
  provider_not_configured: "Proveedor sin configurar", newer_stage_change: "Hubo un cambio más reciente",
  notifications_disabled: "Avisos desactivados", quote_not_pending: "La cotización ya no estaba pendiente", created_before_real_sending: "Creado antes de habilitar envío real",
  sender_domain_mismatch: "Remitente fuera del dominio", qa_allowlist_empty: "Lista QA vacía",
  blocked_not_in_qa_allowlist: "Bloqueado: fuera de lista QA", blocked_extra_recipients: "Bloqueado: destinatarios extra", recipient_changed: "Cambió el correo del cliente",
  provider_rejected_simulated: "Proveedor rechazó el correo (simulado)", provider_unavailable_simulated: "Proveedor no disponible (simulado)",
  internal_error: "Error interno, se reintentará", order_not_found: "Orden no encontrada", tenant_not_found: "Taller no encontrado",
  no_base_url: "Sin dirección del portal", recovered_after_interruption: "Recuperado tras reinicio",
};
const reasonText = (r) => {
  const max = r.startsWith("max_attempts:");
  const code = max ? r.slice(13) : r;
  const label = REASON_LABEL[code] || (code.startsWith("provider_error") ? "Error del proveedor" : code.startsWith("provider_unreachable") ? "Proveedor inaccesible" : code);
  return max ? `Agotó los reintentos · ${label}` : label;
};

function StageChecks({ items, selected, onToggle, prefix, disabled }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {items.map((it) => (
        <label key={it.id} className={`flex items-center gap-2 text-sm ${it.active === false ? "opacity-50" : ""}`}>
          <Checkbox checked={selected.includes(it.id)} disabled={disabled} onCheckedChange={() => onToggle(it.id)} data-testid={`${prefix}-${it.id}`} />
          <span>{it.label}</span>
        </label>
      ))}
    </div>
  );
}

function PreviewDialog({ preview, onClose }) {
  return (
    <Dialog open={!!preview} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="email-preview-dialog">
        <DialogHeader><DialogTitle className="text-base">{preview?.subject}</DialogTitle></DialogHeader>
        {preview && (
          <div className="space-y-2 text-xs text-muted-foreground">
            <div data-testid="email-preview-source">{preview.source === "snapshot" ? "Contenido tal como se envió (enlace oculto)" : "Vista reconstruida con el estado actual de la orden"}</div>
            <div data-testid="email-preview-from">De: {preview.from}</div>
            <div>Para: {preview.to} · Responder a: {preview.reply_to || "—"}</div>
            <iframe title="preview" srcDoc={preview.html} sandbox="" className="w-full h-[420px] border border-border rounded" data-testid="email-preview-html" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeliveryHistory({ rows, onReload, onPreview }) {
  const [shown, setShown] = useState(10);
  return (
    <div className="space-y-2" data-testid="email-history">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Historial de envíos</div>
        <Button size="sm" variant="ghost" onClick={onReload} data-testid="email-history-reload"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      {rows.length === 0 && <div className="text-sm text-muted-foreground" data-testid="email-history-empty">Sin envíos todavía.</div>}
      <div className="divide-y divide-border border border-border rounded-md">
        {rows.slice(0, shown).map((r) => (
          <div key={r.id} className="p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`email-row-${r.id}`}>
            <span className="font-medium w-28">{r.folio || "Prueba"}</span>
            <span className="flex-1 min-w-[140px]">{r.stage_label}</span>
            <span className="text-muted-foreground break-all">{r.recipient || "—"}</span>
            {r.recipient && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => onPreview(r.id)} data-testid={`email-row-preview-${r.id}`}>Ver</Button>}
            <Badge variant={r.status === "failed" ? "destructive" : "secondary"} data-testid={`email-row-status-${r.id}`}>{rowStatus(r)}</Badge>
            <span className="text-xs text-muted-foreground w-full">
              {new Date(r.created_at).toLocaleString("es-MX")} · intentos {r.attempts}
              {r.event_type === "quote_sent" ? (r.resend_attempt ? ` · reenvío #${r.resend_attempt}` : " · cotización enviada") : ""}{r.delivery_mode === "real" ? " · envío real" : ""}{r.reason ? ` · ${reasonText(r.reason)}` : ""}
              {r.portal_link_included === false ? " · sin botón al portal" : ""}
            </span>
          </div>
        ))}
      </div>
      {rows.length > shown && (
        <Button size="sm" variant="outline" onClick={() => setShown(shown + 10)} data-testid="email-history-more">Ver más</Button>
      )}
    </div>
  );
}

export function EmailNotificationsCard() {
  const { hasPermission } = useAuth();
  const { tenantQueryParam } = useTenant();
  const canEdit = hasPermission("settings:edit");
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const openPreview = (id) => api.get(`/email-deliveries/${id}/preview`, { params: tenantQueryParam })
    .then((r) => setPreview(r.data)).catch((e) => toast.error(formatApiError(e)));

  // Stable key: TenantContext may hand a new object with the same content on every render.
  const tq = JSON.stringify(tenantQueryParam || {});
  const dirty = useRef(false);
  const loadRows = useCallback(() => api.get("/email-deliveries", { params: { ...JSON.parse(tq), limit: 100 } })
    .then((r) => setRows(r.data)).catch(() => setRows([])), [tq]);
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/email-settings", { params: JSON.parse(tq) });
      setData(d);
      if (!dirty.current) setForm({ ...d.settings, reply_to: d.settings.reply_to || "" });
    } catch (e) { toast.error(formatApiError(e)); }
    loadRows();
  }, [tq, loadRows]);
  useEffect(() => { load(); }, [load]);

  if (!data || !form) return null;
  const edit = (patch) => { dirty.current = true; setForm((f) => ({ ...f, ...patch })); };
  const toggle = (key, id) => edit({}) || setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));

  async function save() {
    setBusy(true);
    try {
      await api.put("/email-settings", { ...form, reply_to: form.reply_to || null }, { params: tenantQueryParam });
      toast.success("Avisos por correo guardados");
      dirty.current = false;
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function sendTest() {
    try {
      await api.post("/email-settings/test", null, { params: tenantQueryParam });
      toast.success("Prueba enviada a tu correo");
      setTimeout(loadRows, 1500);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <Card className="border-border" data-testid="email-notifications-card">
      <CardHeader>
        <CardTitle className="tracking-tight flex items-center gap-2"><Mail className="h-4 w-4" /> Avisos por correo al cliente</CardTitle>
        <CardDescription>
          Se envían desde {data.provider.from_address} con el nombre de tu taller. Modo actual:{" "}
          <strong data-testid="email-provider-mode">{MODE_LABEL[data.provider.mode] || data.provider.mode}</strong>
          {data.provider.blocking_reason && data.provider.mode !== "simulate" && (
            <span data-testid="email-provider-blocking"> · Simulando: {BLOCKING_LABEL[data.provider.blocking_reason] || data.provider.blocking_reason}</span>
          )}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-3">
          <Switch checked={form.enabled} onCheckedChange={(v) => edit({ enabled: v })} disabled={!canEdit} data-testid="email-enabled-switch" />
          <span className="text-sm">Avisar al cliente cuando cambie la etapa de su vehículo</span>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={!!form.quote_sent_enabled} onCheckedChange={(v) => edit({ quote_sent_enabled: v })} disabled={!canEdit} data-testid="email-quote-switch" />
          <span className="text-sm">Avisar al cliente al enviar una cotización</span>
        </div>
        <div className="space-y-2 max-w-md">
          <Label>Correo de respuesta del taller (reply-to)</Label>
          <Input type="email" value={form.reply_to} onChange={(e) => edit({ reply_to: e.target.value })} disabled={!canEdit} placeholder="contacto@mitaller.mx" data-testid="email-reply-to" />
        </div>
        {data.flows.map((f) => (
          <div key={f.id} className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Flujo: {f.name}</div>
            <StageChecks prefix="email-stage" disabled={!canEdit} selected={form.stage_ids} onToggle={(id) => toggle("stage_ids", id)}
              items={f.stages.map((s) => ({ id: s.id, active: s.active, label: s.customer_label ? `${s.name} → “${s.customer_label}”` : s.name }))} />
          </div>
        ))}
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Órdenes sin flujo (estatus)</div>
          <StageChecks prefix="email-status" disabled={!canEdit} selected={form.base_statuses} onToggle={(id) => toggle("base_statuses", id)}
            items={data.statuses.map((s) => ({ id: s.value, label: s.label }))} />
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={busy} className="btn-tenant" data-testid="email-settings-save">{busy ? "Guardando…" : "Guardar avisos"}</Button>
            <Button variant="outline" onClick={sendTest} data-testid="email-send-test"><Send className="h-4 w-4 mr-2" />Enviar prueba a mi correo</Button>
          </div>
        )}
        <DeliveryHistory rows={rows} onReload={loadRows} onPreview={openPreview} />
        <PreviewDialog preview={preview} onClose={() => setPreview(null)} />
      </CardContent>
    </Card>
  );
}
